import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from './AppIcon';
import { subscribeSchemaErrors, getLatestSchemaError, supabaseConfig } from '../lib/supabase';
import { copySetupSql, SETUP_SQL_STATIC_URL } from '../lib/setupSql';

// ---------------------------------------------------------------------------
// SchemaSetupBanner
//
// A sticky "your database is missing a one-time setup step" strip that
// appears at the very top of the app the moment ANY page hits a
// missing-table or missing-column error against the live database.
//
// Audience: SPECIAL ED TEACHER. Not a developer. Doesn't know what
// "Supabase" or "the schema cache" or "user_profiles" means and shouldn't
// have to. The friendly headline says "one-time database setup", the body
// tells them who can help (often just them, since they're the admin), and
// a set of one-click actions do all the heavy lifting:
//
//   • Finish setup — 3 steps → jumps to /go-live for the guided flow.
//   • Copy setup SQL — puts the whole APPLY_ALL_MIGRATIONS.sql onto the
//     clipboard so it can be pasted into Supabase SQL Editor.
//   • Download setup file — same content as a .sql file for email.
//   • Open Supabase SQL Editor — deep-links to the exact page in
//     Supabase where the SQL should be pasted (uses the project ref
//     baked into the app's VITE_SUPABASE_URL to construct the URL).
//
// The "Show the exact steps" collapse still surfaces the raw table/column
// that's missing for anyone debugging.
//
// The banner is dismissible for the current session (sessionStorage) so a
// teacher can keep exploring the app after they've seen the setup notice,
// but it comes back on next page load until the DB is actually migrated.
//
// Suppressed on /go-live and /setup, since those pages ARE the finish-
// setup UI — stacking the two would look broken.
// ---------------------------------------------------------------------------

// Same-origin static mirror of the bundled migration SQL — used as the
// href for the "Download setup file" link (a direct <a download> is
// smaller code than a JS click handler and lets the browser take care of
// the file naming). The actual clipboard copy uses copySetupSql() from
// setupSql.js which reads from a module-level string (bundled at build
// time), because iOS Safari revokes clipboard permission if you await a
// fetch before writing to the clipboard.
const APP_SQL_URL = SETUP_SQL_STATIC_URL;

const REPO_MIGRATIONS_URL =
  'https://github.com/Senergy-hub/edutracker_pro/blob/main/supabase/APPLY_ALL_MIGRATIONS.sql';

const DISMISS_KEY = 'edutracker.schemaSetup.dismissedFor';

// Derive the Supabase SQL Editor deep-link from the project's Supabase
// URL. Supabase projects live at https://<ref>.supabase.co, and the SQL
// Editor for that project is https://supabase.com/dashboard/project/<ref>/sql/new.
// Returns null when we can't extract a ref (e.g. custom domain, demo mode).
function deriveSqlEditorUrl(supabaseUrl) {
  if (!supabaseUrl || typeof supabaseUrl !== 'string') return null;
  try {
    const u = new URL(supabaseUrl);
    if (!u.hostname.endsWith('.supabase.co')) return null;
    const ref = u.hostname.split('.')[0];
    if (!ref) return null;
    return `https://supabase.com/dashboard/project/${ref}/sql/new`;
  } catch {
    return null;
  }
}

// Turn a schema-error entry into two versions of the same message: a
// friendly teacher-facing summary and a "for your IT person" detail
// block. Teachers never have to look at the second one.
function messagesFor(entry) {
  if (!entry) return null;
  const migration = entry.migration || 'APPLY_ALL_MIGRATIONS.sql';

  return {
    teacherHeadline: 'One-time database setup — takes about a minute',
    teacherBody:
      "The app is connected to your school's database, but the database is missing " +
      "the tables it needs. This is a one-time paste-and-click step. The buttons " +
      "below give you everything needed — or click 'Finish setup' for a guided " +
      "walkthrough.",
    itHeadline:
      entry.kind === 'missing_column'
        ? `Column public.students.${entry.target || '?'} is missing`
        : `Table public.${entry.target || '?'} is missing`,
    itBody: `Run supabase/${migration} against the project. All statements are idempotent.`,
    migration,
  };
}

const SchemaSetupBanner = () => {
  const [entry, setEntry] = useState(getLatestSchemaError());
  const [dismissedAt, setDismissedAt] = useState(() => {
    if (typeof window === 'undefined') return 0;
    try {
      const raw = window.sessionStorage.getItem(DISMISS_KEY);
      return raw ? Number(raw) || 0 : 0;
    } catch {
      return 0;
    }
  });
  const [showTech, setShowTech] = useState(false);
  const [copyStatus, setCopyStatus] = useState('idle'); // 'idle' | 'copying' | 'copied' | 'error'
  const location = useLocation();

  useEffect(() => {
    const unsubscribe = subscribeSchemaErrors((next) => setEntry(next));
    return unsubscribe;
  }, []);

  const rendered = useMemo(() => messagesFor(entry), [entry]);

  const sqlEditorUrl = useMemo(
    () => deriveSqlEditorUrl(supabaseConfig?.url),
    [],
  );

  const handleCopy = useCallback((event) => {
    // MUST stay synchronous. Do not await anything before calling
    // copySetupSql — the SQL is already sitting in a module-level string
    // (see src/lib/setupSql.js), and iOS Safari requires
    // navigator.clipboard.writeText to be called inside the same task as
    // the user gesture that triggered it. If we await a fetch first, iOS
    // silently rejects the copy with "The write permission was denied,"
    // which is the exact bug the field report described.
    setCopyStatus('copying');
    try {
      event?.preventDefault?.();
    } catch {}
    copySetupSql()
      .then((ok) => {
        setCopyStatus(ok ? 'copied' : 'error');
        window.setTimeout(() => setCopyStatus('idle'), 4000);
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error('Failed to copy migration SQL', err);
        setCopyStatus('error');
        window.setTimeout(() => setCopyStatus('idle'), 4000);
      });
  }, []);

  if (!rendered) return null;
  if (entry && entry.seenAt && entry.seenAt <= dismissedAt) return null;

  // Suppress on the Go Live page — that page is a much more useful,
  // dedicated version of what this banner points at, so seeing both
  // stacked is redundant and looks broken.
  if (location?.pathname === '/go-live' || location?.pathname === '/setup') {
    return null;
  }

  const dismiss = () => {
    const now = Date.now();
    setDismissedAt(now);
    if (typeof window !== 'undefined') {
      try {
        window.sessionStorage.setItem(DISMISS_KEY, String(now));
      } catch {
        // sessionStorage disabled — in-memory dismiss still holds for this render.
      }
    }
  };

  return (
    <div
      role="alert"
      className="w-full bg-amber-50 border-b border-amber-200 text-amber-900"
    >
      <div className="max-w-7xl mx-auto flex items-start gap-3 px-4 py-3 text-sm">
        <Icon
          name="Info"
          size={18}
          className="mt-0.5 flex-shrink-0 text-amber-700"
        />
        <div className="flex-1 min-w-0">
          <p className="font-semibold">{rendered.teacherHeadline}</p>
          <p className="mt-1 text-amber-800">
            {rendered.teacherBody}
          </p>

          {/* Primary one-click actions. These are what actually get the
              database migrated — the older "open the setup file on
              GitHub" link just dumped a wall of SQL on a teacher who
              didn't know what to do with it. */}
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Link
              to="/go-live"
              className="inline-flex items-center gap-1.5 rounded bg-amber-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-600 focus:outline-none focus:ring-2 focus:ring-amber-300"
            >
              <Icon name="Rocket" size={12} />
              Finish setup — 3 steps
            </Link>

            <button
              type="button"
              onClick={handleCopy}
              className={`inline-flex items-center gap-1.5 rounded border px-3 py-1.5 text-xs font-semibold transition-colors ${
                copyStatus === 'copied'
                  ? 'border-green-400 bg-green-100 text-green-900'
                  : copyStatus === 'error'
                    ? 'border-red-400 bg-red-100 text-red-900'
                    : 'border-amber-400 bg-amber-100 text-amber-900 hover:bg-amber-200'
              }`}
              disabled={copyStatus === 'copying'}
            >
              <Icon
                name={
                  copyStatus === 'copied'
                    ? 'Check'
                    : copyStatus === 'error'
                      ? 'AlertCircle'
                      : 'Copy'
                }
                size={12}
              />
              {copyStatus === 'copying' && 'Copying…'}
              {copyStatus === 'copied' && 'Setup SQL copied to clipboard'}
              {copyStatus === 'error' && "Couldn't copy — tap Finish setup for a fallback"}
              {copyStatus === 'idle' && 'Copy setup SQL'}
            </button>

            <a
              href={APP_SQL_URL}
              download="EduTrackerPro-database-setup.sql"
              className="inline-flex items-center gap-1.5 rounded border border-amber-300 bg-white px-3 py-1.5 text-xs font-medium text-amber-900 hover:bg-amber-100"
            >
              <Icon name="Download" size={12} />
              Download setup file
            </a>

            {sqlEditorUrl && (
              <a
                href={sqlEditorUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 rounded border border-amber-300 bg-white px-3 py-1.5 text-xs font-medium text-amber-900 hover:bg-amber-100"
              >
                <Icon name="ExternalLink" size={12} />
                Open Supabase SQL Editor
              </a>
            )}

            <button
              type="button"
              onClick={() => setShowTech((v) => !v)}
              className="inline-flex items-center gap-1 rounded px-1 py-1 text-xs font-medium text-amber-800 hover:text-amber-900 underline underline-offset-2"
            >
              {showTech ? 'Hide' : 'Show'} the exact steps
            </button>
          </div>

          {showTech && (
            <div className="mt-3 rounded-md border border-amber-200 bg-white/60 p-3 text-xs text-amber-900">
              <p className="font-semibold">
                Exact steps (you can do this yourself if you're comfortable in Supabase)
              </p>
              <ol className="mt-2 ml-4 list-decimal space-y-1">
                <li>
                  Click <span className="font-semibold">Copy setup SQL</span> above (or download the file).
                </li>
                <li>
                  {sqlEditorUrl ? (
                    <>
                      Click <span className="font-semibold">Open Supabase SQL Editor</span> above — it opens the exact page.
                    </>
                  ) : (
                    <>
                      Sign in to <a href="https://supabase.com/dashboard" target="_blank" rel="noreferrer" className="underline">supabase.com/dashboard</a>, pick this project, and open <span className="font-semibold">SQL Editor → New query</span>.
                    </>
                  )}
                </li>
                <li>
                  Paste the SQL into the editor and click{' '}
                  <span className="font-semibold">Run</span>. It takes about
                  15 seconds.
                </li>
                <li>Reload this page. The banner should be gone.</li>
              </ol>
              <p className="mt-3 border-t border-amber-200 pt-2 font-mono text-[11px] text-amber-800">
                {rendered.itHeadline}. {rendered.itBody} The file is idempotent — safe to re-run if the first attempt only partly succeeded.{' '}
                <a
                  href={REPO_MIGRATIONS_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="underline"
                >
                  View on GitHub
                </a>
                .
              </p>
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss setup banner"
          className="ml-2 flex-shrink-0 rounded p-1 text-amber-700 hover:bg-amber-100 hover:text-amber-900 focus:outline-none focus:ring-2 focus:ring-amber-300"
        >
          <Icon name="X" size={14} />
        </button>
      </div>
    </div>
  );
};

export default SchemaSetupBanner;
